import { parsePayloadObject } from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";

/**
 * Lightweight parse of a tool_call part's raw text into its name and
 * model-provided status message. Handles both the native-JSON protocol and
 * the legacy XML wrapper (ignoring <tool_update> blocks). Returns null when
 * no tool name can be extracted.
 */
export function parseToolCallInfo(
  text: string,
): { name: string; statusMessage?: string } | null {
  const cleaned = text
    .replace(/<tool_update>[\s\S]*?<\/tool_update>/g, "")
    .replace(/^\s*<tool_call>\s*/, "")
    .replace(/\s*<\/tool_call>\s*$/, "")
    .trim();
  const parsed = parsePayloadObject(cleaned);
  if (!parsed || typeof parsed.name !== "string" || !parsed.name.trim()) {
    return null;
  }
  const args = parsed.arguments as Record<string, unknown> | undefined;
  const statusMessage =
    typeof parsed.status_message === "string"
      ? parsed.status_message
      : typeof args?.status_message === "string"
        ? (args.status_message as string)
        : undefined;
  return { name: parsed.name, statusMessage };
}

/**
 * Whether a tool_response part's raw text is fully received. XML-protocol
 * responses stream until their closing tag; native-JSON responses arrive
 * whole.
 */
export function isToolResponseTextComplete(
  text: string,
  isChatConnected: boolean,
): boolean {
  const normalizedText = text.trimStart();
  // Old protocol: check for closing XML tag
  if (normalizedText.includes("</tool_response>")) return true;
  // New protocol: no XML tags, tool response arrives as complete JSON
  if (!normalizedText.startsWith("<tool_response>")) return true;
  // Old protocol still streaming — only complete if disconnected
  return !isChatConnected;
}

/**
 * Parse a complete tool_response part's raw text into its payload.
 * Strips the legacy XML wrapper, then JSON-parses. Generator tools yield
 * multiple ToolResult JSONs back-to-back, so on parse failure the LAST valid
 * JSON object wins (the final result with status_summary). Falls back to the
 * raw string when nothing parses.
 */
export function parseToolResponseText(text: string): any {
  const raw = text
    .replace(/^<tool_response>\s*/, "")
    .replace(/\s*<\/tool_response>$/, "")
    .trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    let lastParsed: any = raw;
    let pos = raw.length - 1;
    // Scan backwards for the last '{' and try to parse from there.
    while (pos >= 0) {
      const braceIdx = raw.lastIndexOf("{", pos);
      if (braceIdx === -1) break;
      try {
        lastParsed = JSON.parse(raw.slice(braceIdx));
        break;
      } catch {
        pos = braceIdx - 1;
      }
    }
    return lastParsed;
  }
}

/**
 * Merge an interactive-prompt outcome (e.g. `user_selection`, `user_upload`)
 * into a tool_response part's raw text, preserving the legacy XML wrapper.
 * Writes into the layer getInnerResult reads: the inner result when wrapped,
 * else top level. Null (and a logged warning) when the text does not parse,
 * so callers can tell a real patch from a no-op.
 */
export function patchToolResponseOutcome(
  text: string,
  outcome: Record<string, unknown>,
): string | null {
  const trimmed = text.trim();
  const hasOpenTag = trimmed.startsWith("<tool_response>");
  const hasCloseTag = trimmed.endsWith("</tool_response>");
  const raw = trimmed
    .replace(/^<tool_response>\s*/, "")
    .replace(/\s*<\/tool_response>$/, "");
  try {
    const parsed = JSON.parse(raw);
    let updated: Record<string, unknown>;
    if (parsed.message_type === "tool_result" && parsed.result) {
      const innerIsString = typeof parsed.result === "string";
      const inner = innerIsString ? JSON.parse(parsed.result) : parsed.result;
      const mergedInner = { ...inner, ...outcome };
      updated = {
        ...parsed,
        result: innerIsString ? JSON.stringify(mergedInner) : mergedInner,
      };
    } else {
      updated = { ...parsed, ...outcome };
    }
    let newText = JSON.stringify(updated);
    if (hasOpenTag) newText = "<tool_response>" + newText;
    if (hasCloseTag) newText = newText + "</tool_response>";
    return newText;
  } catch (e) {
    console.warn(
      "[patchToolResponseOutcome:unparseable] part text left unchanged",
      e,
    );
    return null;
  }
}

/**
 * Whether the dialogue contains a User message after the given message —
 * i.e. the user answered an interactive prompt in plain text, so its open
 * form is stale.
 */
export function hasLaterUserMessage(
  dialogue: Array<{ id?: string; type?: string }> | undefined,
  messageId: string | undefined,
): boolean {
  if (!dialogue || !messageId) return false;
  const idx = dialogue.findIndex((m) => m.id === messageId);
  if (idx === -1) return false;
  return dialogue.slice(idx + 1).some((m) => m.type === "User");
}

// Helper to extract inner result from tool response wrapper.
// Always returns an object or null — primitives (e.g. Math tool returning a
// bare number) are coerced to null so callers can safely use `'X' in result`.
export const getInnerResult = (content: any): any => {
  if (!content || typeof content !== "object") return null;

  // Direct result (unwrapped)
  if (content.type) return content;

  // Standard ToolResult wrapper: {"message_type": "tool_result", "result": {...}}
  if (content.message_type === "tool_result" && content.result !== undefined) {
    try {
      const parsed =
        typeof content.result === "string"
          ? JSON.parse(content.result)
          : content.result;
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Extract the sideband `tool_metadata` object from a tool_response wrapper,
 * if present. Distinct from getInnerResult: tool_metadata is a SIBLING of
 * `result`, not nested inside it, and is never sent to the model.
 */
export const getToolMetadata = (content: any): Record<string, any> | null => {
  if (!content || typeof content !== "object") return null;
  const meta = (content as any).tool_metadata;
  return meta && typeof meta === "object" && !Array.isArray(meta) ? meta : null;
};

/**
 * Tool result types that render their own interactive/rich UI in
 * ToolResponse (prompts, downloads, previews). These must stay visible —
 * never summarized into a compact tool row or hidden inside a collapsed
 * tool-run group.
 */
const INTERACTIVE_RESULT_TYPES = new Set([
  "user_selection_prompt",
  "user_file_upload_prompt",
  "web_search_approval",
  "plan_template_publish_approval",
  "file_download",
  "search_results_display",
  "cr_draft_created",
]);

export function isInteractiveToolResponse(content: any): boolean {
  if (!content || typeof content !== "object") return false;
  if (content.type === "user_profile_form") return true;
  const inner = getInnerResult(content);
  if (!inner) return false;
  if (
    typeof inner.type === "string" &&
    INTERACTIVE_RESULT_TYPES.has(inner.type)
  ) {
    return true;
  }
  // Staged-email preview card — no `type` discriminator, keyed on its
  // distinctive fields (mirrors ToolResponse's stagedEmailData check).
  return (
    inner.email_log_id != null &&
    (typeof inner.to === "string" || typeof inner.subject === "string")
  );
}

/** Unwrap the standard `{message_type: "tool_result", result: ...}` wrapper. */
export function unwrapToolResult(content: any): any {
  if (
    content &&
    typeof content === "object" &&
    content.message_type === "tool_result"
  ) {
    return content.result ?? content;
  }
  return content;
}

/**
 * Format tool output for display.
 * Returns a string representation suitable for monospace rendering.
 */
export function formatToolOutput(result: any): string {
  if (result == null) return "";
  if (typeof result === "string") return result;
  try {
    return JSON.stringify(result, null, 2);
  } catch {
    return String(result);
  }
}

/** Check if the result indicates an error. */
export function isToolErrorResult(result: any): boolean {
  if (!result || typeof result !== "object") return false;
  return "error" in result || result.status === "error";
}

/**
 * Derive a human-readable completion message from tool response content.
 * Checks for explicit `status_summary` from backend first, then falls back
 * to heuristic derivation based on the response structure.
 */
export function deriveCompletionMessage(content: any): string | undefined {
  if (!content) return undefined;

  // Unwrap the standard ToolResult wrapper
  const result =
    typeof content === "object" && content?.message_type === "tool_result"
      ? content
      : content;

  // Priority 1: Backend-provided status_summary
  if (result?.status_summary) return result.status_summary;

  // Priority 1.5: Background task responses (contain correlation_id)
  if (
    typeof result === "object" &&
    result !== null &&
    "correlation_id" in result
  ) {
    if (result.title && typeof result.title === "string") {
      return result.title;
    }
  }

  // Priority 2: Error detection — never expose raw internal errors to users.
  // The model receives the full error and will explain the issue in its response.
  if (typeof result === "object" && result !== null) {
    if ("error" in result || result.status === "error") {
      return undefined;
    }
  }

  // Priority 3: File download
  const inner =
    typeof result === "object" && result?.result ? result.result : result;
  if (inner && typeof inner === "object" && inner.type === "file_download") {
    return `Generated ${inner.filename || "file"}`;
  }

  // Priority 4: Array results (search results, lists, etc.)
  const resultData =
    typeof result === "object" && result?.result !== undefined
      ? result.result
      : null;
  if (Array.isArray(resultData)) {
    return `Found ${resultData.length} result${
      resultData.length !== 1 ? "s" : ""
    }`;
  }

  return undefined;
}
